import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { httpService } from "../../services/httpService";
import { PencilIcon, TrashIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import DynamicTable from "../../components/common/DynamicTable";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import { useProfile } from "../../context/ProfileContext";
import TreatmentPlanDrawer from "./TreatmentPlanDrawer";
import { FaIndianRupeeSign } from "react-icons/fa6";
import ToggleSwitch from "../../components/common/ToggleSwitch"; // Import ToggleSwitch

const MySwal = withReactContent(Swal);

const TreatmentPlan = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const { isPlanExpired } = useProfile();

  useEffect(() => {
    fetchTreatmentPlans();
  }, []);

  useEffect(() => {
    if (!isDrawerOpen) fetchTreatmentPlans();
  }, [isDrawerOpen]);

  const fetchTreatmentPlans = async () => {
    setLoading(true);
    try {
      const response = await httpService.get(
        `/vendorvetclinicservice/get-treatment-plans`,
      );
      if (response.code === 200) {
        setPlans(response?.data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load treatment plans");
    } finally {
      setLoading(false);
    }
  };

  const deletePlan = async (id) => {
    try {
      const response = await httpService.delete(
        `/vendorvetclinicservice/delete-treatment-plan/${id}`,
      );
      if (response.status) {
        toast.success("Treatment plan deleted successfully", {
          position: "top-center",
        });
        fetchTreatmentPlans();
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to delete treatment plan");
    }
  };

  const handleDelete = (id) => {
    MySwal.fire({
      text: "Are you sure you want to delete this treatment plan? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await deletePlan(id);
        MySwal.fire({
          title: "Deleted!",
          text: "Treatment plan has been deleted.",
          icon: "success",
        });
      }
    });
  };

  const handleEdit = (plan) => {
    setSelectedPlanId(plan._id);
    setIsDrawerOpen(true);
  };

  // Updated status toggle handler for Treatment Plan
  const handleStatusToggle = (plan) => {
    if (!plan.can_edit) {
      toast.info("You don't have permission to change this plan's status");
      return;
    }

    // Determine next status based on current status
    const currentStatus = plan.status || "draft";
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    
    // If currently draft, set to active
    const finalStatus = currentStatus === "draft" ? "active" : newStatus;
    
    MySwal.fire({
      text: `Are you sure you want to change status to ${finalStatus}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await httpService.post(
            `/vendorvetclinicservice/change-plan-status/${plan._id}`,
            { status: finalStatus }
          );
          if (response.status) {
            toast.success("Plan status updated successfully!");
            fetchTreatmentPlans();
          }
        } catch (err) {
          console.error(err);
          toast.error(err.response?.data?.message || "Failed to update status");
        }
      }
    });
  };

  const columns = [
    {
      key: "name",
      header: "Plan Name",
      sortable: true,
      renderCell: (row) => (
        <div className="py-2" style={{ minWidth: "200px", maxWidth: "350px" }}>
          <p
            className="text-sm font-medium text-gray-900"
            style={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              lineHeight: "1.5",
            }}
          >
            {row.name || "-"}
          </p>
        </div>
      ),
    },
    {
      key: "procedures",
      header: "Procedures",
      sortable: false,
      renderCell: (row) => {
        const count = row.procedures?.length || 0;
        return (
          <span className="text-sm">
            {count} {count === 1 ? "procedure" : "procedures"}
          </span>
        );
      },
    },
    {
      key: "created_by",
      header: "Created By",
      sortable: false,
      renderCell: (row) => (
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-gray-700">
            {row.created_by?.name || "—"}
          </span>
        </div>
      ),
    },
    {
      key: "total_cost",
      header: "Total Cost",
      sortable: true,
      renderCell: (row) => (
        <div className="flex items-center gap-1">
          <FaIndianRupeeSign className="h-3 w-3" />
          <span className="font-medium">
            ₹{row.total_cost_range?.low || 0}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: false, // Changed to false since we're using a toggle
      renderCell: (row) => (
        <ToggleSwitch
          isActive={row.status === "active"}
          onChange={() => handleStatusToggle(row)}
          disabled={isPlanExpired || !row.can_edit}
        />
      ),
    },
  ];

  const actions = [
    {
      label: "Edit",
      icon: (
        <PencilIcon className="w-5 h-5 text-gray-600 group-hover:text-green-600 transition-colors" />
      ),
      onClick: (row) => handleEdit(row),
      colorClasses: "hover:border-green-500 hover:bg-green-50",
      shouldShow: (row) => !isPlanExpired && row.can_edit,
    },
    {
      label: "Delete",
      icon: (
        <TrashIcon className="w-5 h-5 text-gray-600 group-hover:text-red-600 transition-colors" />
      ),
      onClick: (row) => handleDelete(row._id),
      colorClasses: "hover:border-red-500 hover:bg-red-50",
      shouldShow: (row) => !isPlanExpired && row.can_edit,
    },
  ];

  return (
    <div>
      {loading && <LoaderSpiner text="Loading . . ." />}

      <div className="flex flex-wrap items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Treatment Plans</h1>
          <p className="text-sm text-gray-500 mt-1">
            Group procedures into comprehensive treatment plans
          </p>
        </div>
        <button
          onClick={() => {
            setSelectedPlanId(null);
            setIsDrawerOpen(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Add New Treatment Plan
        </button>
      </div>

      <DynamicTable
        columns={columns}
        data={plans}
        actions={actions}
        showPagination={true}
        initialPageSize={10}
        showPageInfo={true}
        searchable={true}
        searchPlaceholder="Search treatment plans..."
      />

      <TreatmentPlanDrawer
        isOpen={isDrawerOpen}
        planId={selectedPlanId}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedPlanId(null);
        }}
        onSuccess={fetchTreatmentPlans}
      />
    </div>
  );
};

export default TreatmentPlan;